import { useEffect, useState, type CSSProperties } from "react";
import { TableSkeleton } from "../../components/Loading";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supplierFinanceApi, type CombinedLedger } from "../../api/supplierFinance";
import type { ApiError } from "../../api/client";
import { IznosSaValutama } from "../finance/CurrencyBreakdown";
import { fmtDate, money } from "../finance/money";
import { intlLocale } from "../../lib/format";

/**
 * A company Bankom both sells to and buys from, seen from both sides at once.
 * The screen is deliberately inert: it states the position and says plainly
 * that the portal does not post the offset.
 *
 * Each side is every account of the company in euro at the latest rate, with the split by currency
 * under it, as on the finance card; the net is the difference of the two euro figures.
 */
export default function CombinedLedgerPage() {
  const { t, i18n } = useTranslation();
  const locale = intlLocale(i18n.resolvedLanguage);
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<CombinedLedger | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    supplierFinanceApi.combined(id).then(setData).catch((e: ApiError) => setError(e.message));
  }, [id]);

  const kursOd = (dan: string | null) =>
    dan ? t("finance.kpi.rateDate", "at the rate of {{datum}}", { datum: fmtDate(dan) }) : undefined;
  // Each side counts overdue on the day its own ledger reaches, as the card of that side does.
  const naDan = (dan: string) => t("finance.asOf", "stanje na dan {{datum}}", { datum: fmtDate(dan) });

  if (error) return <div className="page"><p style={{ color: "var(--err)" }}>{error}</p></div>;
  if (!data) return <div className="page"><TableSkeleton rows={6} cols={5} label={t("common.loading")} /></div>;

  return (
    <div className="page" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div className="page-head">
        <div>
          <nav className="crumbs" aria-label="Breadcrumb">
            <span>
              <Link to={`/procurement/finance/${data.company.id}`} style={{ color: "var(--ink-3)", textDecoration: "none" }}>
                {data.company.name}
              </Link>
            </span>
          </nav>
          <h1>{t("combinedLedger.title", "Both sides")}</h1>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 12 }}>
        <section style={card}>
          <h2 style={cardTitle}>{t("combinedLedger.customerSide", "As a customer — owes Bankom")}</h2>
          <div style={figure}>
            <IznosSaValutama eur={data.customerSide.balanceTotal} byCurrency={data.customerSide.byCurrency ?? []}
                             uzmi={(v) => v.balance} locale={locale} napomena={kursOd(data.customerSide.rateDate)} tacno />
          </div>
          <p style={sub}>
            {t("combinedLedger.overdue", "Overdue")}: {money(data.customerSide.overdueTotal, data.customerSide.currency)}
            {data.customerAsOf && <> · {naDan(data.customerAsOf)}</>}
          </p>
        </section>
        <section style={card}>
          <h2 style={cardTitle}>{t("combinedLedger.supplierSide", "As a co-operative — Bankom owes")}</h2>
          <div style={figure}>
            <IznosSaValutama eur={data.supplierSide.balanceTotal} byCurrency={data.supplierSide.byCurrency ?? []}
                             uzmi={(v) => v.balance} locale={locale} napomena={kursOd(data.supplierSide.rateDate)} tacno />
          </div>
          <p style={sub}>
            {t("combinedLedger.overdue", "Overdue")}: {money(data.supplierSide.overdueTotal, data.supplierSide.currency)}
            {data.supplierAsOf && <> · {naDan(data.supplierAsOf)}</>}
          </p>
        </section>
        <section style={card}>
          <h2 style={cardTitle}>{t("combinedLedger.netPosition", "Net position")}</h2>
          <p style={figure}>{money(data.netPosition, data.currency)}</p>
          {data.netPosition === null ? (
            <p style={sub}>
              {t("combinedLedger.noRate", "The two sides are kept in different currencies and no exchange rate is on file.")}
            </p>
          ) : (
            kursOd(data.rateDate) && <p style={sub}>{kursOd(data.rateDate)}</p>
          )}
        </section>
      </div>

      <p style={{ ...sub, maxWidth: 640 }}>
        {t(
          "combinedLedger.notPosted",
          "The portal only shows this position. Offsetting the two sides is an accounting decision and is not made here.",
        )}
      </p>
    </div>
  );
}

const card: CSSProperties = {
  background: "var(--surface)",
  border: "1px solid var(--border)",
  borderRadius: "var(--r-md)",
  padding: 18,
};
const cardTitle: CSSProperties = { margin: 0, fontSize: 13, color: "var(--ink-3)", fontWeight: 500 };
const figure: CSSProperties = { margin: "8px 0 0", fontSize: 22, fontWeight: 600 };
const sub: CSSProperties = { margin: "4px 0 0", fontSize: 12, color: "var(--ink-3)" };
